// Imports for external libraries go here.
import React, { FC, useCallback } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { OneUpHighlightModel, OneUpHighlightProps } from './OneUpHighlight.types';
import { StyledOneUpHighlight } from './OneUpHighlight.styles';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import clsx from 'clsx';
import { Button, Image, VideoMolecule } from '@marriott/mi-ui-library';
import DOMPurify from 'isomorphic-dompurify';
import { useDCADynamicModelAlloy } from '../../modules/helpers';
import ReactPlayerLoader from '@brightcove/react-player-loader';

function hasBrightcoveVideoThumbnail(model: OneUpHighlightModel): boolean {
  const t = model.assetProperties?.videoThumbnail;
  return Boolean(t && String(t).trim() !== '');
}

const options = {
  responsive: true,
  autoplay: false,
  muted: true,
  loop: false,
  preload: 'auto',
  src: 'https://players.brightcove.net',
  controls: true,
};

/** Poster URL when no external cover layer: Brightcove thumb, else dynamicThumbnailImage path. */
function getResolvedVideoPoster(model: OneUpHighlightModel): string {
  if (hasBrightcoveVideoThumbnail(model)) {
    return String(model.assetProperties?.videoThumbnail ?? '').trim();
  }
  return model.dynamicThumbnailImage?.assetPath ?? '';
}

/**
 * Phoenix brands: inline ReactPlayerLoader only (no modal).
 * Cover uses mi-ui-library Image from dynamicThumbnailImage (renditions, alt), with fallback URL/alt when needed.
 * Player omits duplicate poster when the cover layer is shown; CSS keeps the native big-play control above the cover.
 */
const PhoenixBrandsInlineVideo: FC<{ model: OneUpHighlightModel }> = ({ model }) => {
  const thumb = model.dynamicThumbnailImage;
  const fallbackPosterUrl = getResolvedVideoPoster(model);
  const coverDefaultUrl =
    (thumb?.assetPath && thumb.assetPath.trim() !== '' ? thumb.assetPath : fallbackPosterUrl) ?? '';
  const coverRenditions = thumb?.renditions ?? [];
  const coverAlt = thumb?.altText ?? model.assetProperties?.thumbnailAltText ?? model.assetProperties?.altText ?? '';
  const showCoverImage = Boolean(coverDefaultUrl.trim()) || (thumb?.renditions?.length ?? 0) > 0;
  /** Avoid duplicating the cover as Video.js poster when the cover layer is used. */
  const playerPoster = showCoverImage ? '' : fallbackPosterUrl;

  const handlePlayerSuccess = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (success: any) => {
      const { ref } = success;
      if (!ref) return;

      ref.ready(() => {
        try {
          const controlBar = ref.getChild?.('ControlBar');
          if (controlBar) {
            const fullscreenToggle = controlBar.getChild?.('FullscreenToggle');
            if (!fullscreenToggle) {
              controlBar.addChild?.('FullscreenToggle', {});
            } else {
              fullscreenToggle.show?.();
            }
          }
        } catch {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.debug('OneUpHighlight: Fullscreen toggle not available for this player');
          }
        }
      });

      const directionValue = document?.documentElement?.getAttribute('dir');
      const videoControllers = ref?.el_?.querySelector?.('.vjs-control-bar');
      if (directionValue === 'rtl' && videoControllers) {
        videoControllers.setAttribute('dir', 'rtl');
      }

      if (model.enableTracking && model.clickTrackValue) {
        const playControlButton = ref.el_?.querySelector?.('.vjs-play-control');
        if (playControlButton) {
          playControlButton.setAttribute('data-custom_click_track_value', model.clickTrackValue);
          playControlButton.classList.add('custom_click_track');
        }
      }
    },
    [model.clickTrackValue, model.enableTracking]
  );

  return (
    <div className="luxury-OneUpHighlight-phoenix-inline-video">
      {showCoverImage && (
        <div className="luxury-OneUpHighlight-phoenix-cover" aria-hidden="true">
          <Image
            customClass="luxury-OneUpHighlight-phoenix-cover-image"
            dynamic={true}
            loading="lazy"
            defaultImageURL={coverDefaultUrl}
            renditions={coverRenditions}
            altText={coverAlt}
          />
        </div>
      )}
      <div className="luxury-OneUpHighlight-video-player">
        <ReactPlayerLoader
          id={`luxury-oneuphighlight-${model.assetProperties?.videoAccountId}`}
          accountId={model.assetProperties?.videoAccountId ?? ''}
          videoId={model.assetProperties?.brightcoveVideoId ?? ''}
          playerId={model.assetProperties?.playerId ?? 'default'}
          poster={playerPoster}
          onSuccess={handlePlayerSuccess}
          options={options}
        />
      </div>
    </div>
  );
};

// Use named rather than default exports.
export const OneUpHighlight: FC<OneUpHighlightProps> = (props: OneUpHighlightProps) => {
  /**
   * Fetching the target response
   */
  const mboxParam = props?.model?.mboxParameter;

  /***
   * Makes the call to target if mboxParam is authored
   */
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;

  /**
   * Replacing the AEM model data with target data if target Response is present
   */
  const componentValues: OneUpHighlightModel = targetData ? targetData : props?.model;

  const sanitizedNode = DOMPurify.sanitize(componentValues?.description as string, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString = sanitizedNode?.innerHTML;

  const textBody = (
    <>
      {componentValues?.headerText && (
        <div
          className={clsx(
            componentValues?.headerSize === 'large'
              ? 't-title-l'
              : componentValues?.headerSize === 'medium'
              ? 't-title-m'
              : 't-title-s'
          )}
        >
          {componentValues.headerText}
        </div>
      )}
      {componentValues?.description && (
        <div className={clsx('t-font-alt-m mt-4')} dangerouslySetInnerHTML={{ __html: fragmentString }}></div>
      )}
      {componentValues?.ctaProperties?.linkUrl && (
        <div className={clsx('d-inline-flex mt-4')}>
          {componentValues.enableTracking && componentValues?.ctaProperties?.linkText && (
            <Button
              isLink={true}
              className={clsx(
                componentValues?.ctaProperties?.ctaType === 'primaryButton' && 'm-button-primary m-button-l px-4',
                componentValues?.ctaProperties?.ctaType === 'secondaryButton' && 'm-button-secondary m-button-l px-4',
                componentValues?.ctaProperties?.ctaType === 'tertiaryLink' &&
                  (componentValues?.ctaProperties?.openInANewTab
                    ? 'm-link-tertiary-button-external'
                    : 'm-link-tertiary-button'),
                componentValues?.ctaProperties?.ctaType === 'textLink' && 'text-link m-link pb-0 pt-2 text-center',
                componentValues?.ctaProperties?.ctaType === 'textLink' &&
                  componentValues?.ctaProperties?.openInANewTab &&
                  'm-link-tertiary-button-external'
              )}
              href={componentValues?.ctaProperties?.linkUrl}
              custom_click_track_value={componentValues.clickTrackValue}
              target={componentValues?.ctaProperties?.openInANewTab ? '_blank' : ''}
              rel={componentValues?.ctaProperties?.openInANewTab ? 'noopener noreferrer' : ''}
              ariaLabel={componentValues?.ctaProperties?.linkText}
            >
              {componentValues?.ctaProperties?.linkText}
            </Button>
          )}
          {!componentValues.enableTracking && componentValues?.ctaProperties?.linkText && (
            <Button
              isLink={true}
              className={clsx(
                componentValues?.ctaProperties?.ctaType === 'tertiaryLink'
                  ? componentValues?.ctaProperties?.openInANewTab
                    ? 'm-link-tertiary-button-external'
                    : 'm-link-tertiary-button'
                  : componentValues?.ctaProperties?.ctaType === 'primaryButton'
                  ? 'm-button-primary m-button-l px-4'
                  : 'm-button-secondary m-button-l px-4',
                componentValues?.ctaProperties?.ctaType === 'textLink' && 'text-link m-link pb-0 pt-2 text-center'
              )}
              href={componentValues?.ctaProperties?.linkUrl}
              target={componentValues?.ctaProperties?.openInANewTab ? '_blank' : ''}
              rel={componentValues?.ctaProperties?.openInANewTab ? 'noopener noreferrer' : ''}
              ariaLabel={componentValues?.ctaProperties?.linkText}
            >
              {componentValues?.ctaProperties?.linkText}
            </Button>
          )}
        </div>
      )}
    </>
  );

  return (
    <StyledOneUpHighlight className={componentValues?.styleclass}>
      <div className={`OneUpHighlight container px-0`}>
        <div
          className={clsx(
            'row px-0 luxury-OneUpHighlight justify-content-center justify-content-xl-start',
            componentValues?.dynamicSecondaryImage && 'flex-column flex-xl-row',
            componentValues?.isPrimaryAssetRight && 'flex-xl-row-reverse'
          )}
        >
          {componentValues && (
            <>
              <div
                className={clsx(
                  'col-10',
                  componentValues?.isPrimaryAssetRight
                    ? 'offset-xl-2 col-xl-5 asset-container'
                    : 'offset-xl-0 col-xl-5',
                  componentValues?.dynamicSecondaryImage &&
                    (componentValues.isPrimaryAssetRight
                      ? 'order-2 order-xl-0 mt-5 mt-xl-0'
                      : 'order-1 order-xl-0 mt-0')
                )}
              >
                {componentValues?.assetProperties?.assetType === 'image' && componentValues?.dynamicImage && (
                  <Image
                    customClass={'luxury-OneUpHighlight-image'}
                    dynamic={true}
                    loading={'lazy'}
                    defaultImageURL={componentValues?.dynamicImage?.assetPath}
                    renditions={componentValues?.dynamicImage?.renditions}
                    altText={componentValues?.dynamicImage?.altText}
                  />
                )}
                {componentValues?.assetProperties?.assetType === 'video' && (
                  <div
                    className={clsx(
                      'luxury-OneUpHighlight-video',
                      componentValues?.assetProperties?.videoThumbnail ? '' : 'no-thumbnail',
                      !componentValues?.openVideoInModal && 'luxury-OneUpHighlight-video--in-placeholder'
                    )}
                  >
                    {!componentValues?.openVideoInModal ? (
                      <PhoenixBrandsInlineVideo model={componentValues} />
                    ) : (
                      <VideoMolecule
                        id={`luxury-oneuphighlight-${componentValues.assetProperties?.videoAccountId}`}
                        accountId={componentValues.assetProperties?.videoAccountId ?? ''}
                        videoId={componentValues.assetProperties?.brightcoveVideoId ?? ''}
                        poster={componentValues.assetProperties?.videoThumbnail ?? ''}
                        playerId={componentValues.assetProperties?.playerId ?? 'default'}
                        enableMute={true}
                        enableVideoLoop={true}
                        renditions={componentValues.dynamicThumbnailImage?.renditions}
                      />
                    )}
                  </div>
                )}
              </div>
              <div
                className={clsx(
                  'col-10',
                  componentValues.isPrimaryAssetRight ? 'offset-xl-0 col-xl-5' : 'offset-xl-2 col-xl-5 text-container',
                  componentValues?.dynamicSecondaryImage
                    ? clsx(
                        componentValues.isPrimaryAssetRight
                          ? 'order-1 order-xl-0 mt-0 pt-0'
                          : 'order-2 order-xl-0 mt-5 pt-0 mt-xl-0',
                        'd-flex flex-column luxury-OneUpHighlight-text-with-secondary',
                        !componentValues.isPrimaryAssetRight && 'flex-column-reverse flex-xl-column'
                      )
                    : 'mt-5 pt-md-3 pt-xl-0 mt-xl-0'
                )}
              >
                {componentValues?.dynamicSecondaryImage && (
                  <div
                    className={clsx(
                      'luxury-OneUpHighlight-secondary-image-wrap',
                      !componentValues.isPrimaryAssetRight && 'luxury-OneUpHighlight-secondary-image-wrap--below-text'
                    )}
                  >
                    <div className="luxury-OneUpHighlight-secondary-image-inner">
                      <Image
                        customClass={'luxury-OneUpHighlight-secondary-image'}
                        dynamic={true}
                        loading={'lazy'}
                        defaultImageURL={componentValues.dynamicSecondaryImage.assetPath}
                        renditions={componentValues.dynamicSecondaryImage.renditions}
                        altText={componentValues.dynamicSecondaryImage.altText}
                      />
                    </div>
                  </div>
                )}
                <div
                  className={clsx(
                    'luxury-OneUpHighlight-text-block flex-shrink-0',
                    componentValues?.alignment === 'center' ? 'text-center' : 'text-left offset-1 offset-xl-0',
                    componentValues?.isPrimaryAssetRight ? 'mb-4 pb-3 pb-xl-0 mb-xl-0' : 'text-block pt-xl-0 mt-xl-0'
                  )}
                >
                  {textBody}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </StyledOneUpHighlight>
  );
};

export const OneUpHighlightConfig = {
  emptyLabel: 'LuxuryOneUpHighlight',
  isEmpty: () => true,
  resourceType: `mi-aem-luxury-spa/components/content/OneUpHighlight/v1/OneUpHighlight`,
};
//TODO:Need to fix the lint issue for editable component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const OneUpHighlightEditable = (props: any) => (
  <div data-testid="luxury-OneUpHighlight" data-component-name="m-luxury-OneUpHighlight">
    <EditableComponent config={OneUpHighlightConfig} {...props}>
      <OneUpHighlight {...props} />
    </EditableComponent>
  </div>
);
